import clsx from 'clsx'
import map from 'lodash/map'
import noop from 'lodash/noop'
import React, { SVGProps, useContext } from 'react'

import SVGAngleRight from '@Assets/SVG/AngleRight'
import SVGClose from '@Assets/SVG/Close'
import { AccordionV2 } from '@Components/CMS/AccordionV2/AccordionV2'
import { FacetContent } from '@Components/Facet/Display/FacetContent'
import { FacetDescriptionButton } from '@Components/Facet/FacetDescription'
import { SVGIcon } from '@Components/Icons'
import { Wordbook } from '@Components/Wordbook'
import { ProductSearchContext } from '@Globals/Algolia/Context'
import { FacetConfig } from '@Globals/Algolia/Types'
import { useTranslation } from '@Globals/Translations'

import styles from './CategoryFilters.module.scss'

const SVGPlus = (props: SVGProps<SVGSVGElement>) => (
  <svg height="100" stroke="black" viewBox="0 0 100 100" width="100" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M 20 50 H 80" strokeWidth="8" />
    <path d="M 50 20 V 80" strokeWidth="8" />
  </svg>
)

const CategoryFilter = ({
  facet,
  mobile,
  onFilterChange,
  renderedIndex,
}: {
  facet: FacetConfig
  mobile: boolean
  onFilterChange?: () => void
  renderedIndex: number
}) => {
  // defaultOpen if not mobile and if the renderedIndex is 1 or 2
  const defaultOpen = !mobile && (renderedIndex === 1 || renderedIndex === 2)

  return (
    <AccordionV2
      className={styles.filterAccordion}
      defaultOpen={defaultOpen}
      id={`facet-filter-${facet.key}`}
      trigger={(
        <div className={styles.filterTrigger}>
          <h3 className={styles.filterTitle}>
            <Wordbook wordbook={facet.name} />
          </h3>
          <SVGPlus />
        </div>
      )}
    >
      <div className={styles.filterContent}>
        {facet.facetDescriptionButton && facet.facetDescriptionIDs && facet.facetDescriptionIDs.length !== 0 && (
          <FacetDescriptionButton className={styles.facetDescriptionTrigger} facetDescriptionIDs={facet.facetDescriptionIDs}>
            <>
              <SVGAngleRight />
              <Wordbook wordbook={facet.facetDescriptionButton} />
            </>
          </FacetDescriptionButton>
        )}
        <FacetContent autoApplyFilter facet={facet} onFilterChange={onFilterChange} />
      </div>
    </AccordionV2>
  )
}

export const CategoryFilters = ({
  className,
  mobile = false,
  onFilterChange = noop,
}: {
  className?: string
  mobile?: boolean
  onFilterChange?: () => void
}) => {
  const { t } = useTranslation()
  const { facetChips, facetConfig } = useContext(ProductSearchContext)

  let renderedIndex = 0

  return (
    <div className={styles.filter}>
      {facetChips && facetChips.length !== 0 && (
        <div className={styles.filteredBy}>
          <div className={styles.filteredByText}>{t('cms:WB_FILTERED_BY')}</div>
          {map(facetChips, ({ id, onClick, text }) => (
            <div
              key={id}
              className={styles.itemsfilteredBy}
              onClick={onClick}
            >
              <p className={styles.itemFilteredText}>
                <Wordbook wordbook={text} />
              </p>
              <SVGIcon icon={SVGClose} size="9px" />
            </div>
          ))}
        </div>
      )}

      <div className={clsx(styles.filters, className)}>
        {map(facetConfig, (facet) => {
          if (!facet || !facet.key || !facet.name) {
            return null
          }

          renderedIndex += 1

          return (
            <CategoryFilter
              key={facet.key}
              facet={facet}
              mobile={mobile}
              onFilterChange={onFilterChange}
              renderedIndex={renderedIndex}
            />
          )
        })}
      </div>
    </div>
  )
}
